import { useMemo, useState } from 'preact/hooks';
import { useLocale, useTranslation } from '../i18n';
import { useFetch } from '../hooks/useFetch';
import { api } from '../api/client';
import { ErrorTile, SectionHeading, Skeleton } from '../primitives';
import { Layout } from '../components/Layout';
import { ArticleCard } from '../components/ArticleCard';
import { publicTags } from '../api/articles';
import type { ArticleListItem } from '../api/types';
import { withBase } from '../paths';

/**
 * Editorial index - a flat archive of published articles, newest first.
 * The legacy SPA shipped this with a tag-filter strip; v2 reproduces
 * the layout so the strip lights up automatically once articles start
 * carrying tags. With zero tags in the current corpus the filter row
 * stays hidden - same UX as the legacy "no chips yet" state.
 *
 * <para>Public, no auth. The backend filters to Published only, so the
 * SPA receives exactly the set readers can see.</para>
 */
export function ArticlesPage() {
    const t = useTranslation();
    const { apiSet } = useLocale();
    const articles = useFetch(() => api.articles.list(apiSet), [apiSet]);
    const [activeTags, setActiveTags] = useState<string[]>([]);

    // Tag inventory across all published articles, sorted by count
    // desc. Hidden when the corpus has no tags. The top 16 cap matches
    // the legacy chip strip's overflow rule.
    const tagInventory = useMemo(() => {
        if (articles.status !== 'success') return new Map<string, number>();
        const counts = new Map<string, number>();
        for (const article of articles.data) {
            for (const tag of publicTags(article)) {
                counts.set(tag, (counts.get(tag) ?? 0) + 1);
            }
        }
        return new Map([...counts.entries()].sort((a, b) => b[1] - a[1]));
    }, [articles]);

    const visible = useMemo(() => {
        if (articles.status !== 'success') return [] as ArticleListItem[];
        if (activeTags.length === 0) return articles.data;
        return articles.data.filter((a) => {
            const tags = new Set(publicTags(a));
            return activeTags.every((t) => tags.has(t));
        });
    }, [articles, activeTags]);

    const toggleTag = (tag: string) => {
        setActiveTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
        );
    };

    return (
        <Layout current="articles">
            <header class="border-b border-rule">
                <div class="mx-auto max-w-[1680px] px-[clamp(20px,3.5vw,56px)] py-[clamp(40px,5vw,72px)]">
                    <SectionHeading
                        eyebrow={t('articles.eyebrow')}
                        title={t('articles.title')}
                        subtitle={
                            articles.status === 'success'
                                ? t('articles.subtitle', { n: articles.data.length })
                                : undefined
                        }
                        size="lg"
                    />

                    {tagInventory.size > 0 && (
                        <div class="mt-6 flex flex-wrap gap-1.5">
                            {[...tagInventory.entries()].slice(0, 16).map(([tag, count]) => {
                                const active = activeTags.includes(tag);
                                return (
                                    <button
                                        key={tag}
                                        type="button"
                                        onClick={() => toggleTag(tag)}
                                        class={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.14em] transition-colors ${
                                            active
                                                ? 'border-accent-soft bg-accent-glow text-accent'
                                                : 'border-rule text-fg-muted hover:border-accent-soft hover:text-fg-primary'
                                        }`}
                                    >
                                        <span>#{tag}</span>
                                        <span class="text-fg-faint">{count}</span>
                                    </button>
                                );
                            })}
                            {activeTags.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setActiveTags([])}
                                    class="inline-flex items-center rounded-full border border-rule px-3 py-1 font-mono text-[10.5px] uppercase tracking-[0.14em] text-fg-faint transition-colors hover:border-accent-soft hover:text-accent"
                                >
                                    {t('articles.clearFilters')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <div class="mx-auto max-w-[1680px] px-[clamp(20px,3.5vw,56px)] py-[clamp(36px,5vw,72px)]">
                {articles.status === 'loading' && (
                    <div class="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton.Line
                                key={i}
                                class="aspect-[4/3] rounded-md"
                                height="100%"
                            />
                        ))}
                    </div>
                )}

                {articles.status === 'error' && (
                    <ErrorTile title={articles.error.message} />
                )}

                {articles.status === 'success' && (
                    <>
                        {visible.length === 0 ? (
                            <div class="grid place-items-center py-16 text-center">
                                <div>
                                    <p class="font-display text-2xl italic text-fg-primary">
                                        {t('articles.noMatchesTitle')}
                                    </p>
                                    <p class="mt-2 text-fg-muted">
                                        {t('articles.noMatchesBody')}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div class="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {visible.map((article) => (
                                    <ArticleCard
                                        key={article.id}
                                        article={article}
                                        rootHref={withBase('/')}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
